import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Search, Wallet, Repeat, RefreshCw, FileText,
  ShieldCheck, User, LogOut, Menu, Landmark, PieChart, CreditCard,
  Target, Star, GitCompare, Calculator, Users, Layers, FolderOpen, Microscope,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { clientApi } from "@/lib/api";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/portfolio", label: "Portfolio", icon: PieChart },
  { to: "/alternates", label: "Alternates", icon: Layers },
  { to: "/research", label: "Research", icon: Microscope },
  { to: "/goals", label: "Goals", icon: Target },
  { to: "/funds", label: "Explore Funds", icon: Search },
  { to: "/compare", label: "Compare", icon: GitCompare },
  { to: "/watchlist", label: "Watchlist", icon: Star },
  { to: "/orders", label: "Orders", icon: Wallet },
  { to: "/payments", label: "Payments", icon: CreditCard },
  { to: "/sips", label: "Systematic Plans", icon: Repeat },
  { to: "/switch", label: "Switch", icon: RefreshCw },
  { to: "/mandates", label: "Mandates", icon: ShieldCheck },
  { to: "/family", label: "Family", icon: Users },
  { to: "/tools", label: "Calculators", icon: Calculator },
  { to: "/statements", label: "Statements", icon: FileText },
  { to: "/documents", label: "Documents", icon: FolderOpen },
  { to: "/profile", label: "Profile", icon: User },
];

function SidebarContent({ pathname, onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-6">
        <Link to="/" className="flex items-center gap-2" onClick={onNavigate}>
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--brand)]">
            <Landmark className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-lg leading-none text-[var(--brand)]">SMIFS</div>
            <div className="text-[11px] tracking-wide text-[var(--ink-muted)]">Mutual Fund Portal</div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
        {NAV_ITEMS.map((item) => {
          const active = pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              data-testid={`nav-${item.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                active ? "bg-[var(--surface-2)] text-[var(--brand)]" : "text-[var(--ink-muted)] hover:bg-[var(--surface-2)] hover:text-[var(--brand)]"
              }`}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-[var(--glass-border)] p-4">
        <div className="mb-2 truncate text-sm font-medium text-[var(--ink)]">{user?.full_name}</div>
        <div className="mb-3 truncate text-xs text-[var(--ink-muted)]">{user?.client_code ? `Client Code: ${user.client_code}` : "KYC not completed"}</div>
        <Button
          variant="outline"
          size="sm"
          className="w-full rounded-full"
          data-testid="client-logout-button"
          onClick={() => { logout(); navigate("/login"); }}
        >
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </Button>
      </div>
    </div>
  );
}

function ExchangeBadge() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let active = true;
    clientApi.get("/bse/status", { showErrorToast: false })
      .then((response) => active && setStatus(response.data))
      .catch(() => active && setStatus({ live: false, unavailable: true }));
    return () => { active = false; };
  }, []);
  if (!status) return <span className="truncate text-xs text-[var(--ink-muted)]">Checking BSE…</span>;
  if (status.unavailable) return <span className="truncate text-xs font-medium text-red-700"><span className="sm:hidden">BSE unavailable</span><span className="hidden sm:inline">BSE status unavailable</span></span>;
  return <div className={`flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium sm:px-3 ${status.live ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`}>
    <span className={`h-2 w-2 shrink-0 rounded-full ${status.live ? "bg-emerald-500" : "bg-red-500"}`} />
    <span className="whitespace-nowrap">BSE {String(status.environment || "").toUpperCase()}</span>
    <span className="hidden whitespace-nowrap sm:inline">· {status.live ? "connected" : "not connected"}</span>
    {status.ucc_status ? <span className="hidden whitespace-nowrap md:inline">· UCC {status.ucc_status}</span> : null}
  </div>;
}

export function ClientLayout({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-dvh smifs-page-wash">
      <div className="flex">
        <aside className="hidden w-[260px] shrink-0 border-r border-[var(--glass-border)] bg-white/70 backdrop-blur-md lg:block">
          <div className="sticky top-0 h-dvh">
            <SidebarContent pathname={location.pathname} />
          </div>
        </aside>

        <div className="flex min-h-dvh min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex min-w-0 items-center justify-between gap-2 border-b border-[var(--glass-border)] bg-white/80 px-3 py-2 backdrop-blur-md sm:px-4 sm:py-3 lg:px-8">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-nav-trigger">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[88vw] max-w-[320px] p-0">
                  <SidebarContent pathname={location.pathname} onNavigate={() => setOpen(false)} />
                </SheetContent>
              </Sheet>
              <span className="font-heading text-base text-[var(--brand)] lg:hidden">SMIFS</span>
            </div>
            <ExchangeBadge />
          </header>
          <main className="min-w-0 flex-1 px-3 py-4 sm:px-4 sm:py-6 lg:px-8 lg:py-8">
            <div className="mx-auto min-w-0 w-full max-w-[1240px]">{children}</div>
          </main>
        </div>
      </div>
    </div>
  );
}
